import { Link } from "react-router-dom";
import { ScrollText, Mail, Phone } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import ContourBackground from "@/components/ContourBackground";
import Reveal from "@/components/Reveal";

const LAST_UPDATED = "March 1, 2026";

export default function Terms() {
  useSeo({
    title: "Terms & Conditions — Tri-Axis XYZ CAD Solutions",
    description:
      "The terms that govern the use of triaxisxyz.com and the CAD drafting / land-survey CAD services delivered by Tri-Axis XYZ CAD Solutions, LLC.",
  });

  return (
    <div data-testid="page-terms">
      <section className="relative overflow-hidden border-b border-slate-200 bg-paper-dark/40">
        <ContourBackground variant="light" opacity={0.5} seed={37} />
        <div className="relative mx-auto max-w-4xl px-6 lg:px-10 py-14 lg:py-20">
          <div className="mono text-[10px] tracking-[0.22em] uppercase text-topo flex items-center gap-2">
            <ScrollText className="h-3 w-3" /> Legal · Terms
          </div>
          <h1 className="display mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900">
            Terms &amp; Conditions
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            These Terms govern your use of <strong>triaxisxyz.com</strong> and any
            CAD drafting or land-survey CAD service we deliver. They are written
            in plain English for clarity; the binding meaning is the natural
            reading of the words.
          </p>
          <p className="mt-3 mono text-[11px] tracking-wide text-slate-500">
            Last updated: {LAST_UPDATED}
          </p>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200 py-14">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 space-y-10">
          <Reveal>
            <Section title="1. Who we are">
              <p>
                <strong>Tri-Axis XYZ CAD Solutions, LLC</strong> ("Tri-Axis", "we",
                "us") is a US-incorporated CAD drafting and land-survey CAD
                services provider operating from Orlando, Florida. By using this
                website or engaging us for any service you agree to these Terms.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="2. Our services">
              <p>
                Tri-Axis delivers production-grade drafting outputs across
                Land-Survey CAD (ALTA/NSPS, boundary, topo, plats, ROW,
                construction staking, as-builts, floodplain certificates, GIS,
                site plans, 3D modeling) and CAD Drafting Beyond Survey
                (architectural, structural, MEP/BIM, mechanical, electrical,
                plumbing/piping, HVAC, civil-engineering, 3D rendering,
                PDF-to-CAD, solar PV, signage). Scope, deliverables, format,
                timeline and price for any specific engagement are agreed in
                writing (email, signed SOW or PO) before work begins.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="3. Use of this website">
              <p>You agree not to:</p>
              <ul className="list-disc pl-5 mt-2 space-y-2">
                <li>Use the website for any unlawful purpose or in a way that breaches these Terms.</li>
                <li>Attempt to access non-public administrative tools or APIs without authorization.</li>
                <li>Scrape, copy or rebrand site content beyond a fair-use evaluation in good faith.</li>
                <li>Upload malicious code, attempt to disrupt the service, or test it for vulnerabilities without our written consent.</li>
              </ul>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="4. Client responsibilities">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Provide accurate and complete inputs (field data, raw
                  survey/CAD files, sketches, reference documents) and respond
                  within reasonable timeframes to clarifying questions.
                </li>
                <li>
                  Confirm that you hold the rights to share any data or
                  intellectual property you provide to us.
                </li>
                <li>
                  Where licensed-professional review or stamping is legally
                  required (Professional Land Surveyor, Professional Engineer,
                  registered Architect, etc.), the responsibility for that
                  review and seal rests with{" "}
                  <strong>your licensed professional in your jurisdiction</strong>.
                  Tri-Axis acts as a production drafter; we do not issue stamps
                  unless explicitly stated in writing for a specific engagement.
                </li>
              </ul>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="5. Intellectual property &amp; deliverables">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Your inputs remain yours.</strong> Field data, point
                  clouds, drawings, photos and any other material you give us
                  remain your property.
                </li>
                <li>
                  <strong>Final deliverables we produce for you become yours</strong>{" "}
                  upon full payment for that engagement. You receive a perpetual,
                  worldwide license to use, modify and re-issue them.
                </li>
                <li>
                  <strong>Our background IP</strong> — internal CAD blocks,
                  scripts, templates, QC checklists, methods and process know-how
                  — remains the property of Tri-Axis.
                </li>
                <li>
                  Website content (copy, illustrations, JSON-LD, code) is owned
                  by Tri-Axis or licensed to us; it may not be republished
                  without our written permission.
                </li>
              </ul>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="6. Fees, invoicing &amp; payment">
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Fees are quoted per project, per drawing, per hour, or as a
                  monthly dedicated-techie retainer, depending on the engagement
                  model agreed in writing.
                </li>
                <li>
                  Unless otherwise agreed, invoices are net-15 from issuance.
                  Late balances may accrue interest at 1.5% per month or the
                  maximum allowed by law.
                </li>
                <li>
                  We may withhold delivery of final files (but not draft
                  previews) until cleared payment is received.
                </li>
              </ul>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="7. Revisions &amp; QC">
              <p>
                Each engagement includes a defined revision allowance (typically
                two rounds for fixed-fee projects, unlimited for hourly /
                retainer engagements). Out-of-scope revisions are quoted before
                work begins. Every deliverable passes an internal QC checklist
                before issuance; we welcome your post-issuance feedback within
                10 business days as part of standard revisions.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="8. Confidentiality">
              <p>
                Project files, design intent, business plans and any sensitive
                information you share with us are treated as confidential. We
                are happy to sign your NDA before commencement, or provide ours.
                Our confidentiality obligation survives termination of any
                engagement.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="9. Disclaimers">
              <p>
                The website is provided "as is" without warranties of any kind.
                Service deliverables are warranted to be produced with reasonable
                professional care and in conformity with the written brief — but
                we make no warranty that they will fit any unstated purpose, nor
                that the licensed professional reviewing them will reach any
                particular regulatory conclusion. Tri-Axis is not a licensed
                surveyor, engineer, or architect of record unless explicitly
                contracted as such in a separate written agreement.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="10. Limitation of liability">
              <p>
                To the maximum extent permitted by law, Tri-Axis's aggregate
                liability arising out of or related to any engagement is limited
                to the fees paid by you for that specific engagement in the 12
                months preceding the claim. We are not liable for indirect,
                incidental, special or consequential damages (including loss of
                profits, business or data) even if advised of their possibility.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="11. Indemnity">
              <p>
                You agree to indemnify and hold Tri-Axis harmless from
                third-party claims arising from (a) inputs or instructions you
                provided that infringe third-party rights, (b) your unauthorized
                use of our deliverables, or (c) your breach of these Terms.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="12. Termination">
              <p>
                Either party may terminate an engagement on written notice; you
                pay for work completed and in-progress up to the termination
                date. We may suspend or terminate access to the website or
                services for serious breach of these Terms.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="13. Governing law">
              <p>
                These Terms are governed by the laws of the State of Florida,
                USA, without reference to its conflict-of-laws rules. The state
                and federal courts located in Orange County, Florida have
                exclusive jurisdiction over any dispute that cannot be resolved
                amicably, except that either party may seek injunctive relief in
                any competent court to protect intellectual property or
                confidential information.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="14. Updates to these Terms">
              <p>
                We may update these Terms as our services and the law evolve.
                The "Last updated" date at the top of this page always shows the
                current version. Continued use of the website or engagement of
                our services after an update constitutes acceptance of the new
                Terms.
              </p>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="15. Contact">
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-emerald-700" />
                  <a className="hover:underline" href="mailto:contact@triaxisxyz.com">
                    contact@triaxisxyz.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-700" />
                  <a className="hover:underline" href="tel:+918083691588">
                    +91 80836 91588
                  </a>
                </li>
              </ul>
              <p className="mt-4 text-sm text-slate-500">
                Also see our{" "}
                <Link to="/privacy" data-testid="terms-to-privacy" className="text-emerald-700 hover:underline">
                  Privacy Policy
                </Link>
                .
              </p>
            </Section>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="display text-xl md:text-2xl font-extrabold tracking-tight text-slate-900">
        {title}
      </h2>
      <div className="mt-3 text-slate-700 leading-relaxed text-[15px]">{children}</div>
    </div>
  );
}
